"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const crypto_1 = __importDefault(require("crypto"));
const starkex_eth_1 = require("@dydxprotocol/starkex-eth");
const starkex_lib_1 = require("@dydxprotocol/starkex-lib");
const lodash_1 = __importDefault(require("lodash"));
const request_helpers_1 = require("../helpers/request-helpers");
const axios_1 = require("../lib/axios");
const db_1 = require("../lib/db");
// TODO: Figure out if we can get rid of this.
const METHOD_ENUM_MAP = {
    [axios_1.RequestMethod.DELETE]: starkex_lib_1.ApiMethod.DELETE,
    [axios_1.RequestMethod.GET]: starkex_lib_1.ApiMethod.GET,
    [axios_1.RequestMethod.POST]: starkex_lib_1.ApiMethod.POST,
    [axios_1.RequestMethod.PUT]: starkex_lib_1.ApiMethod.PUT,
};
const collateralTokenDecimals = 6;
class Private {
    constructor({ host, apiKeyCredentials, starkPrivateKey, networkId, clock, }) {
        this.host = host;
        this.apiKeyCredentials = apiKeyCredentials;
        this.networkId = networkId;
        this.starkLib = new starkex_eth_1.StarkwareLib({}, networkId);
        if (starkPrivateKey) {
            this.starkKeyPair = (0, starkex_lib_1.asSimpleKeyPair)((0, starkex_lib_1.asEcKeyPair)(starkPrivateKey));
        }
        this.clock = clock;
    }
    // ============ Request Helpers ============
    async request(method, endpoint, data) {
        const requestPath = `/v3/${endpoint}`;
        const isoTimestamp = this.clock.getAdjustedIsoString();
        const headers = {
            'DYDX-SIGNATURE': this.sign({
                requestPath,
                method,
                isoTimestamp,
                data,
            }),
            'DYDX-API-KEY': this.apiKeyCredentials.key,
            'DYDX-TIMESTAMP': isoTimestamp,
            'DYDX-PASSPHRASE': this.apiKeyCredentials.passphrase,
        };
        return (0, axios_1.axiosRequest)({
            url: `${this.host}${requestPath}`,
            method,
            data,
            headers,
        });
    }
    async _get(endpoint, params) {
        return this.request(axios_1.RequestMethod.GET, (0, request_helpers_1.generateQueryPath)(endpoint, params));
    }
    async post(endpoint, data) {
        return this.request(axios_1.RequestMethod.POST, endpoint, data);
    }
    async put(endpoint, data) {
        return this.request(axios_1.RequestMethod.PUT, endpoint, data);
    }
    async delete(endpoint, params) {
        return this.request(axios_1.RequestMethod.DELETE, (0, request_helpers_1.generateQueryPath)(endpoint, params));
    }
    // ============ Requests ============
    async get(endpoint, params) {
        return this._get(endpoint, params);
    }
    /**
     * @description get a signature for the ethereumAddress if registered
     */
    async getRegistration(genericParams = {}) {
        return this._get('registration', {
            ...genericParams,
        });
    }
    /**
     * @description get the user associated with the ethereumAddress
     */
    async getUser(genericParams = {}) {
        return this._get('users', {
            ...genericParams,
        });
    }
    /**
     * @description update information for the user
     *
     * @params {
     * @userData specifiying information about the user
     * @email associated with the user
     * @username for the user
     * @isSharingUsername if the user wants their username publicly shared
     * @isSharingAddress if the user wants their ethereumAddress publicly shared
     * @country for the user (ISO 3166-1 Alpha-2 Compliant)
     * @languageCode for the user (ISO 639-1 Compliant, including 'zh-CN')
     * }
     */
    async updateUser({ userData, email, username, isSharingUsername, isSharingAddress, country, languageCode, }) {
        return this.put('users', {
            email,
            username,
            isSharingUsername,
            isSharingAddress,
            userData: JSON.stringify(userData),
            country,
            languageCode,
        });
    }
    /**
     * @description create an account for an ethereumAddress
     *
     * @param starkKey for the account that will be used as the public key in starkwareEx-Lib requests
     * going forward for this account.
     * @param starkKeyYCoordinate for the account that will be used as the Y coordinate for the public
     * key in starkwareEx-Lib requests going forward for this account.
     */
    async createAccount(starkKey, starkKeyYCoordinate) {
        return this.post('accounts', {
            starkKey,
            starkKeyYCoordinate,
        });
    }
    /**
     * @description get account associated with an ethereumAddress and accountNumber 0
     *
     * @param ethereumAddress the account is associated with
     */
    async getAccount(ethereumAddress, genericParams = {}) {
        return this._get(`accounts/${(0, db_1.getAccountId)({ address: ethereumAddress })}`, { ...genericParams });
    }
    /**
     * @description get all accounts associated with an ethereumAddress
     */
    async getAccounts(genericParams = {}) {
        return this._get('accounts', { ...genericParams });
    }
    /**
     * @description get leaderboard pnl for period
     *
     * @param period the period of pnls to retrieve
     */
    async getAccountLeaderboardPnl(period, params, genericParams = {}) {
        return this._get(`accounts/leaderboard-pnl/${period}`, {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get historical leaderboard pnls for period
     *
     * @param period the period of pnls to retrieve
     */
    async getAccountHistoricalLeaderboardPnl(period, params, genericParams = {}) {
        return this._get(`accounts/historical-leaderboard-pnls/${period}`, {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get all positions for an account, meeting query parameters
     *
     * @params {
     * @market the positions are for
     * @status of the positions
     * @limit to the number of positions returned
     * @createdBeforeOrAt latest the positions could have been created
     * }
     */
    async getPositions(params, genericParams = {}) {
        return this._get('positions', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get orders for a user by a set of query parameters
     *
     * @params {
     * @market the orders are for
     * @status the orders have
     * @side of the book the orders are on
     * @type of order
     * @limit to the number of orders returned
     * @createdBeforeOrAt sets the time of the last fill that will be received
     * @returnLatestOrders returns the latest orders instead of the oldest and the order is
     * from most recent to least recent (up to limit)
     * }
     */
    async getOrders(params = {}, genericParams = {}) {
        return this._get('orders', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get active orders (PENDING, OPEN, UNTRIGGERED) for a user by a set of query
     * parameters - if id is included then side is required
     *
     * @params {
     * @market the orders are for
     * @side of the book the orders are on
     * @id of the order
     * }
     */
    async getActiveOrders(market, side, id, genericParams = {}) {
        return this._get('active-orders', {
            market,
            side,
            id,
            ...genericParams,
        });
    }
    /**
     * @description get an order by a unique id
     *
     * @param orderId of the order
     */
    async getOrderById(orderId, genericParams = {}) {
        return this._get(`orders/${orderId}`, { ...genericParams });
    }
    /**
     * @description get an order by a clientId
     *
     * @param clientId of the order
     */
    async getOrderByClientId(clientId, genericParams = {}) {
        return this._get(`orders/client/${clientId}`, { ...genericParams });
    }
    /**
     *@description place a new order
     *
     * @params {
     * @market of the order
     * @side of the order
     * @type of the order
     * @timeInForce of the order
     * @postOnly of the order
     * @size of the order
     * @price of the order
     * @limitFee of the order
     * @expiration of the order
     * @reduceOnly whether the order is reduce-only (optional)
     * @cancelId if the order is replacing an existing one
     * @triggerPrice of the order if the order is a triggerable order
     * @trailingPercent of the order if the order is a trailing stop order
     * }
     * @param positionId associated with the order
     */
    async createOrder(params, positionId, genericParams = {}) {
        const clientId = params.clientId || (0, request_helpers_1.generateRandomClientId)();
        let signature = params.signature;
        if (!signature) {
            if (!this.starkKeyPair) {
                throw new Error('Order is not signed and client was not initialized with starkPrivateKey');
            }
            const orderToSign = {
                humanSize: params.size,
                humanPrice: params.price,
                limitFee: params.limitFee,
                market: params.market,
                side: params.side,
                expirationIsoTimestamp: params.expiration,
                clientId,
                positionId,
            };
            const starkOrder = starkex_lib_1.SignableOrder.fromOrder(orderToSign, this.networkId);
            signature = await starkOrder.sign(this.starkKeyPair);
        }
        const order = {
            ...params,
            clientId,
            signature,
        };
        return this.post('orders', {
            ...order,
            ...genericParams,
        });
    }
    /**
     * @description cancel a specific order for a user by the order's unique id
     *
     * @param orderId of the order being canceled
     */
    async cancelOrder(orderId) {
        return this.delete(`orders/${orderId}`, {});
    }
    /**
     * @description cancel all orders for a user for a specific market
     *
     * @param market of the orders being canceled
     */
    async cancelAllOrders(market) {
        const params = market ? { market } : {};
        return this.delete('orders', params);
    }
    /**
     * @description cancel active orders (PENDING, OPEN, UNTRIGGERED) for a user by a set of query
     * parameters - if id is included then side is required
     *
     * @params {
     * @market the orders are for
     * @side of the book the orders are on
     * @id of the order
     * }
     */
    async cancelActiveOrders(market, side, id, genericParams = {}) {
        return this.delete('active-orders', {
            market,
            side,
            id,
            ...genericParams,
        });
    }
    /**
     *@description get fills for a user by a set of query parameters
     *
     * @params {
     * @market the fills are for
     * @orderId associated with the fills
     * @limit to the number of fills returned
     * @createdBeforeOrAt sets the time of the last fill that will be received
     * }
     */
    async getFills(params, genericParams = {}) {
        return this._get('fills', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get transfers for a user by a set of query parameters
     *
     * @params {
     * @type of transfer
     * @limit to the number of transfers returned
     * @createdBeforeOrAt sets the time of the last transfer that will be received
     * }
     */
    async getTransfers(params = {}, genericParams = {}) {
        return this._get('transfers', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description post a new withdrawal
     *
     * @params {
     * @amount specifies the size of the withdrawal
     * @asset specifies the asset being withdrawn
     * @clientId specifies the clientId for the address
     * }
     * @param positionId specifies the associated position for the transfer
     */
    async createWithdrawal(params, positionId) {
        const clientId = params.clientId || (0, request_helpers_1.generateRandomClientId)();
        let signature = params.signature;
        if (!signature) {
            if (!this.starkKeyPair) {
                throw new Error('Withdrawal is not signed and client was not initialized with starkPrivateKey');
            }
            const withdrawalToSign = {
                humanAmount: params.amount,
                expirationIsoTimestamp: params.expiration,
                clientId,
                positionId,
            };
            const starkWithdrawal = starkex_lib_1.SignableWithdrawal.fromWithdrawal(withdrawalToSign, this.networkId);
            signature = await starkWithdrawal.sign(this.starkKeyPair);
        }
        const withdrawal = {
            ...params,
            clientId,
            signature,
        };
        return this.post('withdrawals', withdrawal);
    }
    /**
     * @description post a new fast-withdrawal
     *
     * @params {
     * @creditAmount specifies the size of the withdrawal
     * @debitAmount specifies the amount to be debited
     * @creditAsset specifies the asset being withdrawn
     * @toAddress is the address being withdrawn to
     * @lpPositionId is the LP positionId for the fast withdrawal
     * @clientId specifies the clientId for the address
     * @signature starkware specific signature for fast-withdrawal
     * }
     */
    async createFastWithdrawal({ lpStarkKey, ...params }, positionId) {
        const clientId = params.clientId || (0, request_helpers_1.generateRandomClientId)();
        let signature = params.signature;
        if (!signature) {
            if (!this.starkKeyPair) {
                throw new Error('Fast withdrawal is not signed and client was not initialized with starkPrivateKey');
            }
            const fact = this.starkLib.factRegistry.getTransferErc20Fact({
                recipient: params.toAddress,
                tokenAddress: this.starkLib.collateralToken.getAddress(),
                tokenDecimals: collateralTokenDecimals,
                humanAmount: params.creditAmount,
                salt: (0, starkex_lib_1.nonceFromClientId)(clientId),
            });
            const transferToSign = {
                senderPositionId: positionId,
                receiverPositionId: params.lpPositionId,
                receiverPublicKey: lpStarkKey,
                factRegistryAddress: this.starkLib.factRegistry.getAddress(),
                fact,
                humanAmount: params.debitAmount,
                clientId,
                expirationIsoTimestamp: params.expiration,
            };
            const starkConditionalTransfer = starkex_lib_1.SignableConditionalTransfer.fromTransfer(transferToSign, this.networkId);
            signature = await starkConditionalTransfer.sign(this.starkKeyPair);
        }
        const fastWithdrawal = {
            ...params,
            clientId,
            signature,
        };
        return this.post('fast-withdrawals', fastWithdrawal);
    }
    /**
     * @description post a new transfer
     *
     * @params {
     * @amount specifies the size of the transfer
     * @receiverAccountId specifies the receiver account id
     * @receiverPublicKey specifies the receiver public key
     * @receiverPositionId specifies the receiver position id
     * @clientId specifies the clientId for the address
     * @signature starkware specific signature for the transfer
     * }
     * @param positionId specifies the associated position for the transfer
     */
    async createTransfer(params, positionId) {
        const clientId = params.clientId || (0, request_helpers_1.generateRandomClientId)();
        let signature = params.signature;
        if (!signature) {
            if (!this.starkKeyPair) {
                throw new Error('Transfer is not signed and client was not initialized with starkPrivateKey');
            }
            const transferToSign = {
                humanAmount: params.amount,
                expirationIsoTimestamp: params.expiration,
                receiverPositionId: params.receiverPositionId,
                senderPositionId: positionId,
                receiverPublicKey: params.receiverPublicKey,
                clientId,
            };
            const starkTransfer = starkex_lib_1.SignableTransfer.fromTransfer(transferToSign, this.networkId);
            signature = await starkTransfer.sign(this.starkKeyPair);
        }
        const transfer = {
            amount: params.amount,
            receiverAccountId: params.receiverAccountId,
            clientId,
            signature,
            expiration: params.expiration,
        };
        return this.post('transfers', transfer);
    }
    /**
     * @description get a user's funding payments by a set of query parameters
     *
     * @params {
     * @market the funding payments are for
     * @limit to the number of funding payments returned
     * @effectiveBeforeOrAt sets the latest funding payment received
     * }
     */
    async getFundingPayments(params, genericParams = {}) {
        return this._get('funding', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get historical pnl ticks for an account between certain times
     *
     * @params {
     * @createdBeforeOrAt latest historical pnl tick being returned
     * @createdOnOrAfter earliest historical pnl tick being returned
     * }
     */
    getHistoricalPnl(params, genericParams = {}) {
        return this._get('historical-pnl', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get trading rewards for a user for a given epoch
     *
     * @params {
     * @epoch to request rewards data for (optional)
     * }
     */
    getTradingRewards(params, genericParams = {}) {
        return this._get('rewards/weight', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get liquidity provider rewards for a user for a given epoch. use for epochs 13+.
     *
     * @params {
     *   @epoch to request rewards data for (optional)
     * }
     *
     */
    getLiquidityProviderRewardsV2(params, genericParams = {}) {
        return this._get('rewards/liquidity-provider', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description (deprecated) get liquidity provider rewards for a user for a given epoch. use for
     * epochs 0-12.
     *
     * @params {
     *   @epoch to request rewards data for (optional)
     * }
     *
     */
    getLiquidityProviderRewards(params, genericParams = {}) {
        return this._get('rewards/liquidity', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get retroactive mining rewards for a user for a given epoch
     *
     */
    getRetroactiveMiningRewards(genericParams = {}) {
        return this._get('rewards/retroactive-mining', {
            ...genericParams,
        });
    }
    /**
     * @description get the key ids associated with an ethereumAddress
     *
     */
    async getApiKeys(genericParams = {}) {
        return this._get('api-keys', { ...genericParams });
    }
    /**
     * @description send verification email to email specified by User
     */
    async sendVerificationEmail() {
        return this.put('emails/send-verification-email', {});
    }
    /**
     * @description requests tokens on dYdX's staging server.
     * NOTE: this will not work on Mainnet/Production.
     */
    async requestTestnetTokens() {
        // Goerli
        if (this.networkId !== 5) {
            throw new Error('Network is not Goerli');
        }
        return this.post('testnet/tokens', {});
    }
    /**
     * @description get ethereum address restrictions on the dYdX protocol.
     */
    async getRestrictions(genericParams = {}) {
        return this._get('restrictions', {
            ...genericParams,
        });
    }
    /**
     * @description comply to dYdX terms of service after a first offense.
     */
    async postRestrictionsCompliance({ residenceCountry, tradingCountry, }, genericParams = {}) {
        return this.post('restrictions/compliance', {
            residenceCountry,
            tradingCountry,
            ...genericParams,
        });
    }
    /**
     * @description get private profile information
     */
    async getProfilePrivate(genericParams = {}) {
        return this._get('profile/private', {
            ...genericParams,
        });
    }
    /**
     * @description get information on active linked users
     */
    async getUserLinks(genericParams = {}) {
        return this._get('users/links', {
            ...genericParams,
        });
    }
    /**
     * @description send a link request action
     */
    async sendLinkRequest(params, genericParams = {}) {
        return this.post('users/links', {
            ...params,
            ...genericParams,
        });
    }
    /**
     * @description get information on pending linked user requests
     */
    async getUserPendingLinkRequests(genericParams = {}) {
        return this._get('users/links/requests', {
            ...genericParams,
        });
    }
    /**
     * @description get a token to send to the Sumsub liveness verification widget
     */
    async postLivenessTokens() {
        return this.post('users/liveness/tokens', {});
    }
    // ============ Signing ============
    sign({ requestPath, method, isoTimestamp, data, }) {
        const messageString = (isoTimestamp +
            METHOD_ENUM_MAP[method] +
            requestPath +
            (lodash_1.default.isEmpty(data) ? '' : JSON.stringify(data)));
        return crypto_1.default.createHmac('sha256', Buffer.from(this.apiKeyCredentials.secret, 'base64')).update(messageString).digest('base64');
    }
}
exports.default = Private;
//# sourceMappingURL=data:application/json;base64,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